import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { approvalsApi } from '@/api/approvals.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { useAccessibleApprovalModules } from '@/hooks/useAccessibleApprovalModules';
import {
  APPROVAL_ENTITY_ACTIONS,
  resolveApprovalModuleFilter,
} from '@/modules/approvals/approvalModuleOptions';
import type { ApprovalStep, ApprovalType, ApprovalWorkflow } from '@/types/approvals';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

const ASSIGNEE_TYPES = [
  { value: 'EMPLOYEE_MANAGER', label: 'Employee Manager' },
  { value: 'MANAGERS_MANAGER', label: "Manager's Manager" },
  { value: 'DEPARTMENT_HEAD', label: 'Department Head' },
  { value: 'HR_MANAGER', label: 'HR Manager' },
  { value: 'FINANCE_MANAGER', label: 'Finance Manager' },
  { value: 'STORE_MANAGER', label: 'Store Manager' },
  { value: 'PROJECT_MANAGER', label: 'Project Manager' },
  { value: 'ROLE', label: 'Specific Role' },
  { value: 'USER', label: 'Specific User' },
];

const CONDITION_FIELDS = [
  'AMOUNT', 'DEPARTMENT', 'LOCATION', 'STORE', 'COMPANY',
  'EMPLOYEE_TYPE', 'LEAVE_TYPE', 'PRIORITY', 'PROJECT', 'BUDGET', 'CATEGORY',
];

const defaultStep = (order: number): ApprovalStep => ({
  stepOrder: order,
  stepGroup: order,
  name: `Step ${order}`,
  approvalMode: 'ALL',
  requiredApprovals: 1,
  assignments: [{ assigneeType: 'EMPLOYEE_MANAGER', sortOrder: 0 }],
});

export function ApprovalBuilderPage() {
  const [searchParams] = useSearchParams();
  const { modules, selectOptions, defaultModule } = useAccessibleApprovalModules({
    includeAll: false,
  });
  const initialModule =
    resolveApprovalModuleFilter(searchParams.get('ownerModule') ?? '', modules) || defaultModule;
  const [workflows, setWorkflows] = useState<ApprovalWorkflow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [ownerModule, setOwnerModule] = useState(initialModule);
  const [entityAction, setEntityAction] = useState('LEAVE');
  const [approvalType, setApprovalType] = useState<ApprovalType>('SEQUENTIAL');
  const [steps, setSteps] = useState<ApprovalStep[]>([defaultStep(1)]);
  const [conditionField, setConditionField] = useState('AMOUNT');
  const [conditionOperator, setConditionOperator] = useState('GTE');
  const [conditionValue, setConditionValue] = useState('');
  const [slaHours, setSlaHours] = useState(48);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setWorkflows(await approvalsApi.listWorkflows());
    } catch (err) {
      showApiError(err, 'Failed to load workflows');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setOwnerModule((current) => {
      const resolved = resolveApprovalModuleFilter(current, modules);
      return resolved || defaultModule;
    });
  }, [modules, defaultModule]);

  useEffect(() => {
    const actions = APPROVAL_ENTITY_ACTIONS[ownerModule] ?? ['GENERIC'];
    if (!actions.includes(entityAction)) {
      setEntityAction(actions[0]);
    }
  }, [ownerModule, entityAction]);

  const accessibleModuleCodes = useMemo(
    () => new Set(modules.map((m) => m.value)),
    [modules],
  );
  const visibleWorkflows = useMemo(
    () => workflows.filter((w) => accessibleModuleCodes.has(w.ownerModule)),
    [workflows, accessibleModuleCodes],
  );

  const resetForm = () => {
    setEditingId(null);
    setCode('');
    setName('');
    setOwnerModule(defaultModule);
    setEntityAction(APPROVAL_ENTITY_ACTIONS[defaultModule]?.[0] ?? 'GENERIC');
    setApprovalType('SEQUENTIAL');
    setSteps([defaultStep(1)]);
    setConditionField('AMOUNT');
    setConditionOperator('GTE');
    setConditionValue('');
    setSlaHours(48);
  };

  const loadWorkflow = (w: ApprovalWorkflow) => {
    setEditingId(w.id);
    setCode(w.code);
    setName(w.name);
    setOwnerModule(w.ownerModule);
    setEntityAction(w.entityAction);
    setApprovalType(w.approvalType);
    setSteps(w.steps.length ? w.steps : [defaultStep(1)]);
    const cond = w.conditions[0];
    if (cond) {
      setConditionField(cond.fieldName);
      setConditionOperator(cond.operator);
      setConditionValue(cond.valueNumber?.toString() ?? cond.valueText ?? '');
    }
    setSlaHours(w.slas[0]?.slaHours ?? 48);
  };

  const addStep = () => {
    setSteps((prev) => [...prev, defaultStep(prev.length + 1)]);
  };

  const save = async () => {
    if (!code.trim() || !name.trim()) {
      notify.error('Code and name are required');
      return;
    }
    setSaving(true);
    try {
      const body = {
        code: code.trim(),
        name: name.trim(),
        ownerModule,
        entityAction,
        approvalType,
        active: true,
        priority: 0,
        conditions: conditionValue
          ? [{
              fieldName: conditionField,
              operator: conditionOperator,
              valueNumber: conditionField === 'AMOUNT' ? Number(conditionValue) : undefined,
              valueText: conditionField !== 'AMOUNT' ? conditionValue : undefined,
              logicGroup: 1,
            }]
          : [],
        steps,
        slas: [{ slaHours }],
        escalations: [],
      };
      if (editingId) {
        await approvalsApi.updateWorkflow(editingId, body);
        notify.success('Workflow updated');
      } else {
        await approvalsApi.createWorkflow(body);
        notify.success('Workflow created');
      }
      resetForm();
      load();
    } catch (err) {
      showApiError(err, 'Failed to save workflow');
    } finally {
      setSaving(false);
    }
  };

  const workflowColumns: Column<ApprovalWorkflow>[] = [
    { key: 'ownerModule', header: 'Module' },
    { key: 'entityAction', header: 'Action' },
    { key: 'name', header: 'Name' },
    { key: 'approvalType', header: 'Type' },
    {
      key: 'active',
      header: 'Status',
      render: (row) => (row.active ? 'Active' : row.archived ? 'Archived' : 'Inactive'),
    },
    {
      key: 'id',
      header: '',
      render: (row) => (
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" onClick={() => loadWorkflow(row)}>
            Edit
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={async () => {
              try {
                await approvalsApi.cloneWorkflow(row.id);
                notify.success('Workflow cloned');
                load();
              } catch (err) {
                showApiError(err, 'Clone failed');
              }
            }}
          >
            Clone
          </Button>
          {row.active ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={async () => {
                try {
                  await approvalsApi.deactivateWorkflow(row.id);
                  notify.success('Deactivated');
                  load();
                } catch (err) {
                  showApiError(err, 'Deactivate failed');
                }
              }}
            >
              Deactivate
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="page-content">
      <PageHeader
        title="Approval Workflows"
        description="Design approval chains without code — sequential, parallel, or hybrid"
        breadcrumbs={[
          { label: 'Settings', to: '/work' },
          { label: 'Approval Workflows' },
        ]}
      />

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="card space-y-4 p-4">
          <h2 className="text-lg font-semibold">
            {editingId ? 'Edit workflow' : 'Create workflow'}
          </h2>

          <div className="grid gap-3 sm:grid-cols-2">
            <Input label="Code" value={code} onChange={(e) => setCode(e.target.value)} />
            <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
            <Select
              label="Module"
              value={ownerModule}
              onChange={setOwnerModule}
              options={selectOptions}
            />
            <Select
              label="Action"
              value={entityAction}
              onChange={setEntityAction}
              options={(APPROVAL_ENTITY_ACTIONS[ownerModule] ?? ['GENERIC']).map((a) => ({
                value: a,
                label: a.replace(/_/g, ' '),
              }))}
            />
            <Select
              label="Approval type"
              value={approvalType}
              onChange={(v) => setApprovalType(v as ApprovalType)}
              options={[
                { value: 'SEQUENTIAL', label: 'Sequential' },
                { value: 'PARALLEL', label: 'Parallel' },
                { value: 'HYBRID', label: 'Hybrid' },
              ]}
            />
            <Input
              label="SLA (hours)"
              type="number"
              value={slaHours}
              onChange={(e) => setSlaHours(Number(e.target.value))}
            />
          </div>

          <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
            <p className="mb-2 text-sm font-medium">Condition (optional)</p>
            <div className="grid gap-2 sm:grid-cols-3">
              <Select
                value={conditionField}
                onChange={setConditionField}
                options={CONDITION_FIELDS.map((f) => ({ value: f, label: f }))}
              />
              <Select
                value={conditionOperator}
                onChange={setConditionOperator}
                options={[
                  { value: 'EQ', label: 'Equals' },
                  { value: 'GTE', label: '>=' },
                  { value: 'GT', label: '>' },
                  { value: 'LTE', label: '<=' },
                  { value: 'LT', label: '<' },
                  { value: 'IN', label: 'In list' },
                ]}
              />
              <Input
                placeholder="Value"
                value={conditionValue}
                onChange={(e) => setConditionValue(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">Approval steps</p>
              <Button size="sm" variant="secondary" onClick={addStep}>
                Add step
              </Button>
            </div>
            {steps.map((step, idx) => (
              <div
                key={idx}
                className="rounded-lg border border-slate-200 p-3 dark:border-slate-700"
              >
                <Input
                  label={`Step ${step.stepOrder} name`}
                  value={step.name}
                  onChange={(e) =>
                    setSteps((prev) =>
                      prev.map((s, i) => (i === idx ? { ...s, name: e.target.value } : s)),
                    )
                  }
                />
                {approvalType === 'HYBRID' ? (
                  <Input
                    label="Step group (same group = parallel)"
                    type="number"
                    value={step.stepGroup ?? step.stepOrder}
                    onChange={(e) =>
                      setSteps((prev) =>
                        prev.map((s, i) =>
                          i === idx ? { ...s, stepGroup: Number(e.target.value) } : s,
                        ),
                      )
                    }
                    className="mt-2"
                  />
                ) : null}
                <Select
                  label="Approver"
                  className="mt-2"
                  value={step.assignments[0]?.assigneeType ?? 'EMPLOYEE_MANAGER'}
                  onChange={(assigneeType) =>
                    setSteps((prev) =>
                      prev.map((s, i) =>
                        i === idx
                          ? {
                              ...s,
                              assignments: [
                                {
                                  ...s.assignments[0],
                                  assigneeType,
                                },
                              ],
                            }
                          : s,
                      ),
                    )
                  }
                  options={ASSIGNEE_TYPES}
                />
                {step.assignments[0]?.assigneeType === 'ROLE' ? (
                  <Input
                    label="Role code"
                    className="mt-2"
                    value={step.assignments[0]?.roleCode ?? ''}
                    onChange={(e) =>
                      setSteps((prev) =>
                        prev.map((s, i) =>
                          i === idx
                            ? {
                                ...s,
                                assignments: [
                                  { ...s.assignments[0], roleCode: e.target.value },
                                ],
                              }
                            : s,
                        ),
                      )
                    }
                  />
                ) : null}
              </div>
            ))}
          </div>

          <div className="flex gap-2">
            <Button onClick={save} disabled={saving}>
              {editingId ? 'Update workflow' : 'Create workflow'}
            </Button>
            {editingId ? (
              <Button variant="secondary" onClick={resetForm}>
                Cancel
              </Button>
            ) : null}
          </div>
        </div>

        <div className="card p-4">
          <h2 className="mb-3 text-lg font-semibold">Existing workflows</h2>
          <DataTable
            columns={workflowColumns}
            data={visibleWorkflows}
            keyField="id"
            loading={loading}
            emptyMessage="No workflows yet — create your first approval chain"
          />
        </div>
      </div>
    </div>
  );
}
